import { supabase } from '../lib/supabase';
import { sendPushToClient } from './pushUtils.js';
import { t } from '../i18n/nl.js';

/**
 * Blokkeer een client: zet blocked_at/blocked_by op profiles,
 * zet actieve enrollments op 'blocked', stuur notificaties.
 */
export async function blockClient(clientId, coachUserId) {
  // 1+2. Profiel blokkeren + actieve enrollments→'blocked' + audit-log,
  //      atomisch en server-side via SECURITY DEFINER RPC (HERSTEL 7 / F4-8.5).
  const { error: blockErr } = await supabase.rpc('block_client', {
    p_client_id: clientId,
  });
  if (blockErr) throw blockErr;

  // 3. Haal client profiel op (voor naam in mail/coach-push)
  const { data: profile } = await supabase
    .from('profiles')
    .select('full_name')
    .eq('id', clientId)
    .maybeSingle();

  // 4. Push naar client — niet-uitschakelbaar (financiële/compliance-status).
  try {
    await sendPushToClient(
      supabase, coachUserId, clientId, null,
      t('blocking.clientBlockedPushTitel'),
      t('blocking.clientBlockedPushBody')
    );
  } catch { /* best-effort */ }

  // 5. E-mail naar client
  const { data: clientEmail } = await supabase.rpc('get_client_email', { p_client_id: clientId });
  if (clientEmail) {
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: clientEmail,
          subject: t('blocking.clientBlockedEmailSubject'),
          html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:20px">
            <h2 style="color:#dc2626">${t('blocking.clientBlockedEmailHeading')}</h2>
            <p>${t('blocking.clientBlockedEmailGreeting').replace('{naam}', profile?.full_name || t('blocking.fallbackNaam'))}</p>
            <p>${t('blocking.clientBlockedEmailBody1')}</p>
            <p>${t('blocking.clientBlockedEmailBody2')}</p>
            <p>${t('blocking.clientBlockedEmailBody3')}</p>
            <br/>
            <p>${t('blocking.emailSignatureVriendelijk')}</p>
          </div>`,
        },
      });
    } catch { /* best-effort */ }
  }

  // 6. Notificatie naar coaches van deze client — niet-uitschakelbaar
  //    (twijfelgeval, zelfde compliance-redenering als de clientpush hierboven).
  const { data: coachLinks } = await supabase
    .from('coach_clients')
    .select('coach_id')
    .eq('client_id', clientId)
    .eq('status', 'active');

  for (const link of coachLinks || []) {
    try {
      await sendPushToClient(
        supabase, coachUserId, link.coach_id, null,
        t('blocking.coachBlockedPushTitel'),
        t('blocking.coachBlockedPushBody').replace('{naam}', profile?.full_name || t('blocking.fallbackClientNaam'))
      );
    } catch { /* best-effort */ }
  }

  return { success: true };
}

/**
 * Deblokkeer een client: verwijder blocked_at/blocked_by,
 * herstel blocked enrollments waar mogelijk.
 */
export async function unblockClient(clientId, coachUserId) {
  // 1. Profiel deblokkeren + audit-log, atomisch en server-side via
  //    SECURITY DEFINER RPC (HERSTEL 7 / F4-8.5).
  const { error: unblockErr } = await supabase.rpc('unblock_client', {
    p_client_id: clientId,
  });
  if (unblockErr) throw unblockErr;

  // 2. Haal client profiel op (voor naam in mail/coach-push)
  const { data: profile } = await supabase
    .from('profiles')
    .select('full_name')
    .eq('id', clientId)
    .maybeSingle();

  // 3. Herstel blocked enrollments — atomisch via SECURITY DEFINER RPC
  //    (capaciteitscheck met row-lock + creditrestitutie bij volle lessen).
  const { data: restoreResult, error: restoreErr } = await supabase.rpc(
    'restore_blocked_enrollments',
    { p_client_id: clientId }
  );
  if (restoreErr) throw restoreErr;

  const restored = restoreResult?.restored || [];
  const removed = restoreResult?.removed || [];

  // 4. Push/mail per herstelde les
  const { data: clientEmail } = await supabase.rpc('get_client_email', { p_client_id: clientId });

  for (const e of restored) {
    const lesNaam = e.lesson_name || t('blocking.fallbackLesNaam');
    const lesDatum = e.starts_at
      ? new Date(e.starts_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
      : '';
    try {
      await sendPushToClient(
        supabase, coachUserId, clientId, 'lessons_schedule',
        t('blocking.lessonRestoredPushTitel'),
        t('blocking.lessonRestoredPushBody').replace('{les}', lesNaam).replace('{datum}', lesDatum)
      );
    } catch { /* best-effort */ }
  }

  for (const e of removed) {
    const lesNaam = e.lesson_name || t('blocking.fallbackLesNaam');
    const lesDatum = e.starts_at
      ? new Date(e.starts_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
      : '';
    try {
      await sendPushToClient(
        supabase, coachUserId, clientId, 'lessons_schedule',
        t('blocking.lessonRemovedPushTitel'),
        t('blocking.lessonRemovedPushBody').replace('{les}', lesNaam).replace('{datum}', lesDatum)
      );
    } catch { /* best-effort */ }
  }

  // 5. Algemene bevestiging push + mail naar client — niet-uitschakelbaar.
  try {
    await sendPushToClient(
      supabase, coachUserId, clientId, null,
      t('blocking.clientUnblockedPushTitel'),
      t('blocking.clientUnblockedPushBody')
    );
  } catch { /* best-effort */ }

  if (clientEmail) {
    try {
      await supabase.functions.invoke('send-email', {
        body: {
          to: clientEmail,
          subject: t('blocking.clientUnblockedEmailSubject'),
          html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:20px">
            <h2 style="color:#16a34a">${t('blocking.clientUnblockedEmailHeading')}</h2>
            <p>${t('blocking.clientBlockedEmailGreeting').replace('{naam}', profile?.full_name || t('blocking.fallbackNaam'))}</p>
            <p>${t('blocking.clientUnblockedEmailBody1')}</p>
            ${restored.length > 0 ? `<p>${t('blocking.clientUnblockedEmailRestoredNote').replace('{count}', restored.length)}</p>` : ''}
            ${removed.length > 0 ? `<p>${t('blocking.clientUnblockedEmailRemovedNote').replace('{count}', removed.length)}</p>` : ''}
            <br/>
            <p>${t('blocking.emailSignatureSportief')}</p>
          </div>`,
        },
      });
    } catch { /* best-effort */ }
  }

  // 6. Notificatie naar coaches — niet-uitschakelbaar (zelfde twijfelgeval
  //    als bij blockClient hierboven).
  const { data: coachLinks } = await supabase
    .from('coach_clients')
    .select('coach_id')
    .eq('client_id', clientId)
    .eq('status', 'active');

  for (const link of coachLinks || []) {
    try {
      await sendPushToClient(
        supabase, coachUserId, link.coach_id, null,
        t('blocking.coachUnblockedPushTitel'),
        t('blocking.coachUnblockedPushBody').replace('{naam}', profile?.full_name || t('blocking.fallbackClientNaam'))
      );
    } catch { /* best-effort */ }
  }

  return { success: true, restored: restored.length, removed: removed.length };
}

/**
 * Controleer of alle collections-facturen van een client betaald zijn.
 */
export async function allCollectionsInvoicesPaid(clientId) {
  const { data, error } = await supabase
    .from('invoices')
    .select('id')
    .eq('client_id', clientId)
    .eq('status', 'collections');

  if (error) throw error;
  return (!data || data.length === 0);
}
